import { colors } from '@lmc/common/constants'
import { CernerRole } from '@lmc/ts-common'
import { capitalize } from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'
import { useSelector } from 'react-redux'
import { LinkComponent, ProfileField } from '../../../../../components/generic'
import { ResidentProfileSectionWrapper } from '../../../components'

/**
@description Renders the team member's permissions.

@param {object} features. The home's features.

{
    eMAR: true / false
}

@param {object} profile. The team member's profile object. Contains their
permissions:
{
    medicationPermission: true / false
}

@returns {function} TeamMemberProfilePermissions.
*/

export default function TeamMemberProfilePermissions(props) {
  const { features, profile } = props

  const home = useSelector((state) => state.global.home)

  const isEmarEnabled = features?.features?.eMAR?.enabled
  const isCernerEnabled = features?.features?.cerner?.enabled
  const isGpConnectEnabled = !!home?.odsCode && !!home?.asidCode

  // Always show permissions section if cernerRole is present or if eMAR/GP Connect are enabled
  if (!(isEmarEnabled || isGpConnectEnabled || profile.cernerRole)) {
    return null
  }

  return (
    <ResidentProfileSectionWrapper
      antdIcon="KeyOutlined"
      color={colors.darkblue}
      style={styles.wrapper}
      title="Permissions"
    >
      <div className="lmc-card" style={styles.contentContainer}>
        {PERMISSION_INFO_FIELDS.filter((field) => {
          if (field.key === 'medicationPermission' && !isEmarEnabled)
            return false
          if (field.key === 'gpConnectPermission' && !isGpConnectEnabled)
            return false
          if (field.key === 'cernerRole' && !isCernerEnabled) return false
          return true
        }).map((field, i) => {
          return (
            <ProfileField
              key={i}
              label={field.label}
              style={styles.field}
              value={getValue(field.key, profile, features)}
            />
          )
        })}
      </div>
    </ResidentProfileSectionWrapper>
  )
}

const getValue = (key, profile, features) => {
  const value = profile[key]

  if (key === 'medicationPermission' && !features.features?.eMAR?.enabled) {
    return (
      <LinkComponent target="_blank" to="/settings/billing">
        eMAR Feature <span className="octicon octicon-chevron-right" />
      </LinkComponent>
    )
  }

  if (key === 'gpConnectPermission') {
    return value ? 'Clinical' : 'Non-Clinical'
  }

  if (key === 'cernerRole') {
    const roleLabels = {
      [CernerRole.NoAccess]: 'No Access',
      [CernerRole.SeniorCarer]: 'Senior Carer Access',
      [CernerRole.Admin]: 'Admin Access',
      [CernerRole.RegisteredManager]: 'Registered Manager Access'
    }
    return roleLabels[value] || 'No Access'
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }

  if (!value) return <span style={styles.noInfo}>No information entered</span>

  return capitalize(profile[key])
}

const PERMISSION_INFO_FIELDS = [
  {
    key: 'medicationPermission',
    label: 'Medication'
  },
  {
    key: 'gpConnectPermission',
    label: 'GP Connect Access'
  },
  {
    key: 'cernerRole',
    label: 'London Care Record Access Level'
  }
]

const styles = {
  emarAd: {
    color: colors.pink
  },
  field: {
    textAlign: 'left'
  },
  noInfo: {
    color: colors.black
  },
  wrapper: {}
}

TeamMemberProfilePermissions.propTypes = {
  profile: PropTypes.object.isRequired
}
